import {defineStore} from "pinia";
import {useEnterpriseGatewayApiProxy} from "@/plugins/proxy-client-plugin.ts";
import {LoadingState} from "@/app/stores/loading-state.ts";
import {DashboardDto, HealthCheckDto, MessageQueueCountDto} from "@/api/enterprise-gateway-api.proxy.ts";
import {useErrorStore} from "@/app/stores/error-store.ts";
import {defineComponent, ref} from "vue";
import type {RouteRecordRaw} from "vue-router";
import {AdminProcessDetail} from "@/app/admin/admin-routes.ts";

const BUSINESS_CENTRAL_SERVICE_NAME = 'BusinessCentralHealthCheck';
const CLEARING_HOUSE_SERVICE_NAME = 'ClearingHouseHealthCheck';
const ACCOUNT_SERVICE_NAME = 'ApiVersionHealthCheck';
type ServiceType = typeof BUSINESS_CENTRAL_SERVICE_NAME | typeof CLEARING_HOUSE_SERVICE_NAME;

export enum ProcessCounterType {
    SessionsWithoutInvoice
}

export interface IProcessCounterData {
    counter: number;
    counterType: ProcessCounterType;
    counterText: string;
}

export const useMonitoringStore = defineStore('monitoring-store', () => {
    const apiProxy = useEnterpriseGatewayApiProxy();
    const loadingState = new LoadingState();
    const errorStore = useErrorStore();
    const rabbitMqServices = ref<MessageQueueCountDto[]>([]);
    const dashBoard = ref<DashboardDto>(new DashboardDto());
    const businessCentralService = ref<MessageQueueCountDto | undefined>();
    const clearingHouseService = ref<MessageQueueCountDto | undefined>();
    const counters = ref<IProcessCounterData[]>([]);
    const isRemovingDeadLetters = ref<boolean>(false);
    const isQueueingDeadLetters = ref<boolean>(false);

    const SERVICE_DISPLAY_NAMES: Record<ServiceType, string> = {
        [BUSINESS_CENTRAL_SERVICE_NAME]: 'Business Central',
        [CLEARING_HOUSE_SERVICE_NAME]: 'Clearing House',
    };

    let queueIntervalId: number;
    let dashboardIntervalId: number;

    const pollQueueData = async () => {
        rabbitMqServices.value = await apiProxy.operations_MessageQueueCount();
    };

    const pollDashboard = async () => {
        dashBoard.value = await apiProxy.operations_Dashboard();
        await loadHealthChecks()
    }

    const findService = (healthChecks: HealthCheckDto[], serviceName: ServiceType): MessageQueueCountDto | undefined => {
        const serviceHealthCheck = healthChecks.find(h => h.name === serviceName);

        if (!serviceHealthCheck) {
            return undefined;
        }

        return new MessageQueueCountDto({
            name: SERVICE_DISPLAY_NAMES[serviceName],
            errorCount: 0,
            activeCount: 0,
            serviceHealth: serviceHealthCheck.serviceHealth,
        });

    }
    
    const requeueDeadLetters = async (queueName: string, messageIds: Array<string>) => {
        try {
            isQueueingDeadLetters.value = true;
            await apiProxy.operations_RequeueDeadLetters(queueName, messageIds);
            isQueueingDeadLetters.value = false;
        } catch (e) {
            isQueueingDeadLetters.value = false;
            loadingState.setError();
            await errorStore.setError(e, true);
        }
    }

    const deleteDeadLetters = async (queueName: string, messageIds: Array<string>) => {
        try {
            isRemovingDeadLetters.value = true;
            await apiProxy.operations_DeleteDeadLetters(queueName, messageIds);
            isRemovingDeadLetters.value = false;
        } catch (e) {
            isRemovingDeadLetters.value = false;
            loadingState.setError();
            await errorStore.setError(e, true);
        }
    }
    
    const deadLetterQueueDetails = async (queueName: string) => {
        try {
            loadingState.startLoading();
            const deadLetterDetails = await apiProxy.operations_DeadLetterQueueDetails(queueName);
            loadingState.stopLoading();
            return deadLetterDetails;
        } catch (e) {
            loadingState.setError();
            await errorStore.setError(e, true);
            return [];
        }
    }

    const loadHealthChecks = async () => {
        const healthCheck = await apiProxy.operations_HealthChecks();
        const serviceHealth = healthCheck.find(h => h.name === ACCOUNT_SERVICE_NAME);
        
        for(let i=0; i<rabbitMqServices.value.length; i++)
        {
            rabbitMqServices.value[0].serviceHealth = serviceHealth?.serviceHealth ?? rabbitMqServices.value[0].serviceHealth;
        }
        
        businessCentralService.value = findService(healthCheck, BUSINESS_CENTRAL_SERVICE_NAME);
        clearingHouseService.value = findService(healthCheck, CLEARING_HOUSE_SERVICE_NAME);
    }

    const loadCounter = async () => {
        counters.value = [];
        counters.value.push(
            {
                counterType: ProcessCounterType.SessionsWithoutInvoice,
                counter: dashBoard.value.anomalies.sessionsWithoutInvoice,
                counterText: 'ontbrekende factuurnummers'
            }
        );
    }

    const loadProcessCounter = async (processCounterType: ProcessCounterType) => {
        try {
            loadingState.startLoading();
            const anomalies = await apiProxy.operations_Anomalies();
            loadingState.stopLoading();
            return processCounterType === ProcessCounterType.SessionsWithoutInvoice ?
                anomalies.sessionsWithoutInvoice ?? [] : [];
        } catch (e) {
            loadingState.setError();
            await errorStore.setError(e, true);
        }
    }

    const startDashboardPolling = async () => {
        try {
            loadingState.startLoading();
            dashBoard.value = await apiProxy.operations_Dashboard();
            await loadHealthChecks();
            await loadCounter();
            dashboardIntervalId = setInterval(() => {
                pollDashboard();
            }, 20000);
            loadingState.stopLoading();
        } catch (e) {
            loadingState.setError();
            await errorStore.setError(e, true);
        }
    }

    const startQueuePolling = async () => {
        try {
            loadingState.startLoading();
            rabbitMqServices.value = await apiProxy.operations_MessageQueueCount();
            queueIntervalId = setInterval(() => {
                pollQueueData();
            }, 40000);
            loadingState.stopLoading();
        } catch (e) {
            loadingState.setError();
            await errorStore.setError(e, true);
        }
    };

    const stopQueuePolling = () => {
        clearInterval(queueIntervalId);
    }

    const stopDashboardPolling = () => {
        clearInterval(dashboardIntervalId);
    }

    return {
        rabbitMqServices,
        dashBoard,
        counters,
        businessCentralService,
        clearingHouseService,
        isLoading: loadingState.isLoading,
        errorLoading: loadingState.errorLoading,
        isRemovingDeadLetters,
        isQueueingDeadLetters,
        deadLetterQueueDetails,
        startQueuePolling,
        stopQueuePolling,
        startDashboardPolling,
        stopDashboardPolling,
        loadProcessCounter,
        requeueDeadLetters,
        deleteDeadLetters
    }
});